import { FlexComponentProps, FlexComponentModel, FlexComponent } from "typings/flexFramework/FlexComponent";
import { ClientWizardStateStore } from "src/stores/wizard/ClientWizardStateStore";
import { AnalyticsStore, ContextStore, RouteStore } from "bernie-core";
import { PackageConfig } from "stores/wizard/config";
import * as React from "react";
import { CommonWizardFlexModel } from "typings/wizard/CommonWizardFlexModel";
import { PackageWizardState, ThirdPartyPackageWizardState } from "stores/wizard/state";
import { UitkLayoutConditionalGridSpan } from "uitk-react-layout-grid";
import { LocalizedComponentProps } from "@shared-ui/localization-context";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { UitkCheckbox } from "uitk-react-switch";
import { CompositionStore } from "stores/CompositionStore";

export type onSubLOBSelection = (pwaSubLOB: string) => () => void;

export type setPackageType = () => string;

export interface WizardPackagePWAFlexComponent extends FlexComponent {
  model: WizardPackagePWAFlexModuleResult;
}

export interface WizardPackagePWAFlexModuleResult extends FlexComponentModel, CommonWizardFlexModel {
  include3PP: boolean;
  includePageHeading: boolean;
  includePageSubHeading: boolean;
  defaultPackageSubLOB: string;
  packageTypes: string[];
}

export interface WizardPackagePWAProps extends FlexComponentProps {
  flexComponent: WizardPackagePWAFlexComponent;
  wizardState: ClientWizardStateStore;
  routeStore: RouteStore;
  analytics?: AnalyticsStore;
  compositionStore?: CompositionStore;
}

export interface WizardPackagePWAState {
  isSubLOBFlightTypeMenuOpen: boolean;
  isPreferredClassMenuOpen: boolean;
}

export interface WizardPackagePWAViewState {
  currentTab: string;
}

export interface WizardPackagePWAViewProps extends WizardPackagePWAProps, LocalizedComponentProps {
  setPackageType: () => string;
  setFlightType: () => string;
  hasSubLOBParam: () => boolean;
  toggleStaySomewhereElse: () => void;
  toggleDriveSomewhereElse: () => void;
  togglePartialStay: () => void;
  defaultPackageType: LobCombinations;
  onSubLOBSelection: (pwaSubLOB: string) => () => void;
  isSubLOBFlightTypeMenuOpen: boolean;
  onSubLOBFlightTypeMenuTriggerClick: () => void;
  onSubLOBFlightTypeMenuItemClick: (pwaSubLOBFlightType: string) => () => void;
  isPreferredClassMenuOpen: boolean;
  onSelectPreferredClassCode: (classCode: string) => void;
  routeStore: RouteStore;
  getTargetURL: (param: string, value: string) => string;
  getParam: (param: string) => string;
  packageSubLOBs: Map<string, string[]>;
  getLocError?: (isLink: boolean) => string | undefined;
  setInputsRefs?: (ref: React.RefObject<HTMLInputElement>, secondRef?: React.RefObject<HTMLInputElement>) => void;
}

export interface PackageSubLOBProps extends WizardPackagePWAProps {
  packageType?: string;
  packageConfig: PackageConfig;
  packageWizardState: PackageWizardState;
  toggleStaySomewhereElse: () => void;
  toggleDriveSomewhereElse: () => void;
  togglePartialStay: () => void;
  setFlightType?: (selectEvent: React.ChangeEvent) => void;
  setInputsRefs?: (ref: React.RefObject<HTMLInputElement>) => void;
}

export interface HotelCarSubLOBProps extends Omit<PackageSubLOBProps, "displayPreferredClassCode"> {}

export interface PackageHiddenSearchDataProps {
  pwaSubLOB: string;
  packageWizardState: PackageWizardState;
  flightClass: string;
  leg1: string;
  leg2?: string;
  config: PackageConfig;
}

export interface StaySomewhereElseProps {
  lobState: PackageWizardState;
  context?: ContextStore;
  routeStore?: RouteStore;
  colSpan?: UitkLayoutConditionalGridSpan;
}

export interface StaySomewhereElseToggleProps {
  toggleStaySomewhereElse: () => void;
  packageWizardState: PackageWizardState;
}

export interface DriveSomewhereElseToggleProps {
  toggleDriveSomewhereElse: () => void;
  packageWizardState: PackageWizardState;
}

export interface PackagePartialStayProps {
  togglePartialStay: () => void;
  packageWizardState: PackageWizardState;
}
export interface PackagePreferredClassProps {
  packageWizardState: PackageWizardState;
  setFlightClass: (selectEvent: React.ChangeEvent) => void;
}

export type LobType = "f" | "h" | "c";
export type LobCombinations = "fh" | "fhc" | "fc" | "hc";

export interface ILOBCheckbox {
  label: string;
  altLabelAdd: string;
  altLabelRemove: string;
  checked: boolean;
  ref: React.RefObject<UitkCheckbox>;
  id: string;
  type: LobType;
}

export interface PackageTypeMenuCheckboxesProps extends LocalizedComponentProps {
  lobState: PackageWizardState;
  onSubLOBSelection: onSubLOBSelection;
  setPackageType: setPackageType;
  defaultPackageType: LobCombinations;
  updateLobsMissing: React.Dispatch<number>;
  minimumLobsNeeded: number;
  getTargetURL: (param: string, value: string) => string;
  packageSubLOBs: Map<string, string[]>;
  currentSubLOB?: string;
  showOneway: boolean;
  showMulticity: boolean;
  onSubLOBMenuTriggerClick: () => void;
  onSubLOBMenuItemClick: (pwaSubLOB: string) => () => void;
  setFlightType: () => string;
}

export interface ILOBMap {
  flights: ILOBCheckbox;
  hotels: ILOBCheckbox;
  cars: ILOBCheckbox;
}

export interface IPackageType {
  checked: boolean;
  type: LobType;
}

export interface IPackageTypeMap {
  flights: IPackageType;
  hotels: IPackageType;
  cars: IPackageType;
}

export interface PackageTypeMenuCheckboxesState {
  lobMap: ILOBMap;
  error: boolean;
  useLinkPill: boolean;
  packageTypeDefinition: IPackageTypeMap;
  activeSubLOB?: string;
}

export interface PackageExperiment {
  control: boolean;
  variant1: boolean;
}

export interface PackageLegInformation {
  originTla: string;
  originId: string;
  destinationTla: string;
  destinationId: string;
  startDate: string;
  endDate: string;
}

export interface ThirdPartyPackageProps extends Omit<WizardPackagePWAProps, "context"> {
  thirdPartyPackageWizardState: ThirdPartyPackageWizardState;
  flexComponent: WizardPackagePWAFlexComponent;
  context?: ExtendedContextStore;
  getLocError?: (isLink: boolean) => string | undefined;
  setInputsRefs?: (ref: React.RefObject<HTMLInputElement>, secondRef?: React.RefObject<HTMLInputElement>) => void;
}

export interface OffersWithTransferToggleProps {
  lobState: ThirdPartyPackageWizardState;
}

export enum PackagesSubLobs {
  FLIGHTS_HOTEL = "fh",
  FLIGHTS_CAR = "fc",
  FLIGHTS_HOTEL_CAR = "fhc",
  HOTEL_CAR = "hc",
}
